/**
 * Speaker Featured Media: the video lightbox, and the Books & Media reveal.
 *
 * The grid ships with no iframes at all — nine cards would otherwise mean nine
 * third-party players loading before anyone asked to watch one. Each card is a
 * link carrying the provider and id in data attributes, and this file builds
 * exactly ONE iframe at a time.
 *
 * That single-iframe rule is what keeps two videos from ever sounding at once:
 * moving between videos replaces the stage's contents rather than hiding a
 * previous player, and closing empties the stage entirely. A hidden iframe
 * keeps playing; a removed one cannot.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'iframe',
	'[tabindex]:not([tabindex="-1"])',
].join( ',' );

const SWIPE_THRESHOLD = 50;

/**
 * Whether the visitor has asked for reduced motion.
 *
 * @return {boolean} True when animation should be skipped.
 */
function reducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * The player URL for a provider and id.
 *
 * Built here from a whitelisted host and an encoded id rather than from
 * anything stored, so a bad value in the field cannot become a live src.
 *
 * @param {string} provider "vimeo" or "youtube".
 * @param {string} id       Video id.
 * @return {string} Embed URL.
 */
function embedUrl( provider, id ) {
	const safeId = encodeURIComponent( id );

	if ( 'vimeo' === provider ) {
		return `https://player.vimeo.com/video/${ safeId }?autoplay=1&dnt=1`;
	}

	return `https://www.youtube-nocookie.com/embed/${ safeId }?autoplay=1&rel=0`;
}

/**
 * Read a provider and id out of a watch URL.
 *
 * The mirror of the theme's PHP parser
 * (damelio_network_2026_wordpress_theme_hero_parse_embed), and deliberately the
 * same two patterns: anything either one accepts, the other does too. It exists
 * because the "Outline + Video Icon" button carries a plain link an author typed
 * into the editor — there is no data attribute to read, and asking the server to
 * annotate every such button would mean parsing markup on every render.
 *
 * Returns null for anything that is not one of the two providers, which is what
 * leaves an ordinary link an ordinary link.
 *
 * @param {string} href The link's href.
 * @return {?{provider: string, id: string}} Provider and id, or null.
 */
function parseVideoUrl( href ) {
	if ( ! href ) {
		return null;
	}

	const youtube = href.match(
		/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
	);

	if ( youtube ) {
		return { provider: 'youtube', id: youtube[ 1 ] };
	}

	const vimeo = href.match( /vimeo\.com\/(?:video\/)?(\d+)/ );

	if ( vimeo ) {
		return { provider: 'vimeo', id: vimeo[ 1 ] };
	}

	return null;
}

/**
 * Every opener that carries its video in its own href.
 *
 * Two kinds, and they differ only in where the caption comes from. An "Outline +
 * Video Icon" button is a button whose own label ("Watch Video") says nothing
 * about what plays, so it captions only from an author-set `title`. A Video Card
 * already shows the video's name on the poster, and hands that over directly —
 * a `title` there would add a tooltip nobody asked for.
 *
 * For both, the style or the block is what marks the element as wanting the
 * player, and the href is what decides whether it gets one: anything that is not
 * YouTube or Vimeo stays an ordinary link.
 *
 * @return {Array<{el: HTMLElement, item: Object}>} Openers and their one-video sets.
 */
function videoButtons() {
	const selector = [
		'.wp-block-button.is-style-dn-video > .wp-block-button__link[href]',
		'a.dn-video-card__link[href]',
	].join( ',' );

	return Array.from( document.querySelectorAll( selector ) )
		.map( ( el ) => {
			const parsed = parseVideoUrl( el.getAttribute( 'href' ) );

			if ( ! parsed ) {
				return null;
			}

			// A card states its caption; a button has to have been given one.
			if ( el.hasAttribute( 'data-dn-video-caption' ) ) {
				const cardCaption = el.getAttribute( 'data-dn-video-caption' ).trim();

				return {
					el,
					item: {
						provider: parsed.provider,
						id: parsed.id,
						caption: cardCaption,
						title: cardCaption || el.textContent.trim(),
						poster: '',
					},
				};
			}

			// The button's own label — "Watch Video" — says nothing about the
			// video, so it is not printed under the player. The link's title
			// attribute is, when an author has set one; with none there is no
			// caption at all. `caption` is stated even when empty, because
			// mount() falls back to `title` only for the openers that DON'T
			// carry the key: a speaker's video has a real name and still shows
			// it, and this had to stay true of them.
			const caption = ( el.getAttribute( 'title' ) || '' ).trim();

			return {
				el,
				item: {
					provider: parsed.provider,
					id: parsed.id,
					caption,
					// The iframe still needs a non-empty accessible name, so
					// that one does fall back to the label. textContent, so a
					// button whose rich text carries markup still yields a
					// plain string.
					title: caption || el.textContent.trim(),
					poster: '',
				},
			};
		} )
		.filter( Boolean );
}

/**
 * Wire the lightbox to the video cards on the page.
 *
 * @param {HTMLElement} dialog The lightbox element.
 */
function initLightbox( dialog ) {
	// Three kinds of opener, one dialog.
	//
	// A speaker page has a grid of links, and the set is the whole grid. A
	// speaker-grid block has one button per card, and each carries its own
	// speaker's library — so a single page can play any of a dozen libraries
	// without shipping a dozen dialogs. A "Watch Video" button is a set of one,
	// read off its own href. Everything past this point works on whichever set
	// was opened and does not care which kind opened it.
	const links = Array.from( document.querySelectorAll( 'a[data-dn-video]' ) );
	const cards = Array.from( document.querySelectorAll( '[data-dn-video-set]' ) );
	const buttons = videoButtons();

	if ( ! links.length && ! cards.length && ! buttons.length ) {
		return;
	}

	// Bound once per dialog, whatever loads this file.
	//
	// The speaker page gets it as the layout block's viewScript; a page carrying
	// a speaker-grid block enqueues it by handle. A page with both would run it
	// twice, and a second pass would bind a second click handler to every opener
	// — two dialogs' worth of work on one dialog, and a rail rebuilt twice per
	// click. Cheaper to make the function idempotent than to reason about which
	// pages can hold which blocks.
	if ( dialog.dataset.dnVideoBound ) {
		return;
	}

	dialog.dataset.dnVideoBound = '1';

	const stage = dialog.querySelector( '.dn-speaker__lightbox-stage' );
	const titleEl = dialog.querySelector( '.dn-speaker__lightbox-title' );
	const rail = dialog.querySelector( '.dn-speaker__lightbox-rail' );

	if ( ! stage ) {
		return;
	}

	// The set currently loaded, and the rail drawn from it. Both are replaced
	// wholesale by loadSet(); nothing here is derived once at startup, because
	// the next thing clicked may be a different speaker entirely.
	let items = [];
	let railButtons = [];

	let current = -1;
	let lastFocused = null;

	/**
	 * One item's poster URL, from a page grid link.
	 *
	 * The link already contains the poster the server chose — which may have
	 * cost a remote lookup — so it is read back out rather than resolved again.
	 *
	 * @param {Element} link A [data-dn-video] anchor.
	 * @return {string} The poster src, or ''.
	 */
	function posterFrom( link ) {
		const img = link.querySelector( 'img' );

		return img ? img.getAttribute( 'src' ) || '' : '';
	}

	/**
	 * Replace the loaded set and redraw the rail to match.
	 *
	 * @param {Array} next Items with provider, id, title and poster.
	 */
	function loadSet( next ) {
		items = Array.isArray( next ) ? next : [];

		// A set of one has nothing to step through and nothing to pick from, so
		// the rail and its arrows are hidden rather than shown pointing back at
		// the video already playing.
		dialog.classList.toggle( 'is-single', items.length < 2 );

		if ( ! rail ) {
			railButtons = [];
			return;
		}

		rail.replaceChildren();

		items.forEach( ( item, index ) => {
			const li = document.createElement( 'li' );
			const button = document.createElement( 'button' );

			li.className = 'dn-speaker__rail-item';
			button.type = 'button';
			button.className = 'dn-speaker__rail-button';
			button.setAttribute( 'data-dn-video-goto', String( index ) );
			button.setAttribute( 'aria-current', 'false' );

			if ( item.poster ) {
				const img = document.createElement( 'img' );

				img.className = 'dn-speaker__video-poster';
				img.src = item.poster;
				img.alt = '';
				img.loading = 'lazy';
				img.decoding = 'async';
				button.appendChild( img );
			}

			// textContent, never innerHTML: a title is speaker-entered content
			// and reaches this script through a JSON attribute.
			const label = document.createElement( 'span' );

			label.className = 'screen-reader-text';
			label.textContent = item.title || '';
			button.appendChild( label );

			li.appendChild( button );
			rail.appendChild( li );
		} );

		railButtons = Array.from( rail.querySelectorAll( '[data-dn-video-goto]' ) );
	}

	const isOpen = () => ! dialog.hasAttribute( 'hidden' );

	/**
	 * Replace the stage with a fresh player for one item.
	 *
	 * @param {number} index Index into items.
	 */
	function mount( index ) {
		const item = items[ index ];

		if ( ! item ) {
			return;
		}

		// Removing the old iframe is what stops the old video. Emptying first
		// also means a slow-loading player never overlaps its predecessor.
		stage.replaceChildren();

		const iframe = document.createElement( 'iframe' );

		iframe.src = embedUrl( item.provider, item.id );
		iframe.title = item.title;
		iframe.allow = 'autoplay; fullscreen; picture-in-picture';
		iframe.allowFullscreen = true;
		iframe.referrerPolicy = 'strict-origin-when-cross-origin';
		iframe.setAttribute( 'frameborder', '0' );

		/*
		 * The stage is empty until the player paints, which on a slow connection
		 * is a black rectangle for several seconds with nothing to say a video
		 * is on its way. `is-loading` puts a spinner there until the iframe
		 * fires load.
		 *
		 * `error` clears it too: a video that is private, deleted or blocked
		 * never loads, and a spinner that turns forever reads as a broken page
		 * rather than a missing video.
		 */
		stage.classList.add( 'is-loading' );

		const settle = () => stage.classList.remove( 'is-loading' );

		iframe.addEventListener( 'load', settle, { once: true } );
		iframe.addEventListener( 'error', settle, { once: true } );

		stage.appendChild( iframe );

		if ( titleEl ) {
			// `caption ?? title`, not `caption || title`: an opener that sets
			// the key to an empty string is saying "no caption", while one that
			// never sets it at all (a speaker page's grid, a speaker card) still
			// gets its video's name.
			const caption = item.caption ?? item.title;

			titleEl.textContent = caption;
			titleEl.hidden = '' === caption;
		}

		railButtons.forEach( ( button, i ) => {
			const isCurrent = i === index;

			button.setAttribute( 'aria-current', isCurrent ? 'true' : 'false' );
			button.parentElement?.classList.toggle( 'is-current', isCurrent );

			if ( isCurrent ) {
				button.scrollIntoView( {
					behavior: reducedMotion() ? 'auto' : 'smooth',
					block: 'nearest',
					inline: 'center',
				} );
			}
		} );

		current = index;
	}

	function open( index ) {
		lastFocused = document.activeElement;
		dialog.removeAttribute( 'hidden' );
		document.body.classList.add( 'dn-modal-open' );
		mount( index );

		const close = dialog.querySelector( '.dn-speaker__lightbox-close' );

		if ( close ) {
			close.focus();
		}
	}

	function close() {
		if ( ! isOpen() ) {
			return;
		}

		// Empty the stage before hiding: a hidden iframe keeps playing.
		stage.replaceChildren();
		stage.classList.remove( 'is-loading' );
		dialog.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'dn-modal-open' );

		if ( titleEl ) {
			titleEl.textContent = '';
		}

		if ( lastFocused && 'function' === typeof lastFocused.focus ) {
			lastFocused.focus();
		}

		current = -1;
	}

	/**
	 * Move by a number of videos, wrapping at both ends.
	 *
	 * @param {number} by -1 or 1.
	 */
	function step( by ) {
		if ( ! items.length ) {
			return;
		}

		mount( ( current + by + items.length ) % items.length );
	}

	// A speaker page's grid: every link is one set, opened at the one clicked.
	const pageSet = links.map( ( link ) => ( {
		provider: link.getAttribute( 'data-dn-video-provider' ) || '',
		id: link.getAttribute( 'data-dn-video-id' ) || '',
		title: link.getAttribute( 'data-dn-video-title' ) || '',
		poster: posterFrom( link ),
	} ) );

	links.forEach( ( link, index ) => {
		link.addEventListener( 'click', ( event ) => {
			// Modified clicks stay a plain link to the video's own page.
			if ( event.metaKey || event.ctrlKey || event.shiftKey || 1 === event.button ) {
				return;
			}

			event.preventDefault();
			loadSet( pageSet );
			open( index );
		} );
	} );

	// A speaker card: its own speaker's library, opened at the first video.
	cards.forEach( ( card ) => {
		card.addEventListener( 'click', ( event ) => {
			let set;

			try {
				set = JSON.parse( card.getAttribute( 'data-dn-video-set' ) || '[]' );
			} catch ( error ) {
				// Malformed data leaves the control inert rather than opening an
				// empty dialog, which would read as the video being broken.
				return;
			}

			if ( ! Array.isArray( set ) || ! set.length ) {
				return;
			}

			event.preventDefault();
			loadSet( set );
			open( 0 );
		} );
	} );

	// A "Watch Video" button: its own href, played on its own.
	//
	// The rail ends up with a single thumbnail-less entry, and the steppers wrap
	// straight back to it — which is right. This button says "watch this one",
	// not "browse a library".
	buttons.forEach( ( { el, item } ) => {
		el.addEventListener( 'click', ( event ) => {
			// Modified clicks stay a plain link to the video's own page, the
			// same bargain the speaker grid's links strike.
			if ( event.metaKey || event.ctrlKey || event.shiftKey || 1 === event.button ) {
				return;
			}

			event.preventDefault();
			loadSet( [ item ] );
			open( 0 );
		} );
	} );

	dialog.addEventListener( 'click', ( event ) => {
		const target = event.target;

		if ( target.closest( '[data-dn-video-close]' ) ) {
			event.preventDefault();
			close();
			return;
		}

		const stepper = target.closest( '[data-dn-video-step]' );

		if ( stepper ) {
			event.preventDefault();
			step( parseInt( stepper.getAttribute( 'data-dn-video-step' ), 10 ) || 1 );
			return;
		}

		const goto = target.closest( '[data-dn-video-goto]' );

		if ( goto ) {
			event.preventDefault();
			mount( parseInt( goto.getAttribute( 'data-dn-video-goto' ), 10 ) || 0 );
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( ! isOpen() ) {
			return;
		}

		if ( 'Escape' === event.key ) {
			close();
			return;
		}

		if ( 'ArrowLeft' === event.key ) {
			event.preventDefault();
			step( -1 );
			return;
		}

		if ( 'ArrowRight' === event.key ) {
			event.preventDefault();
			step( 1 );
			return;
		}

		if ( 'Tab' !== event.key ) {
			return;
		}

		const focusable = Array.from( dialog.querySelectorAll( FOCUSABLE ) ).filter(
			( el ) => null !== el.offsetParent
		);

		if ( ! focusable.length ) {
			return;
		}

		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );

	// Swipe between videos on touch. Bound to the panel rather than the stage,
	// because the stage is an iframe and swallows its own pointer events.
	let touchStartX = null;

	const panel = dialog.querySelector( '.dn-speaker__lightbox-panel' );

	if ( panel ) {
		panel.addEventListener(
			'touchstart',
			( event ) => {
				touchStartX = event.changedTouches[ 0 ].clientX;
			},
			{ passive: true }
		);

		panel.addEventListener(
			'touchend',
			( event ) => {
				if ( null === touchStartX ) {
					return;
				}

				const delta = event.changedTouches[ 0 ].clientX - touchStartX;

				touchStartX = null;

				if ( Math.abs( delta ) < SWIPE_THRESHOLD ) {
					return;
				}

				step( delta < 0 ? 1 : -1 );
			},
			{ passive: true }
		);
	}
}

/**
 * Animate a list between two heights, then hand height back to the layout.
 *
 * @param {HTMLElement} list  The element to animate.
 * @param {number}      from  Starting height in px.
 * @param {number}      to    Target height in px.
 */
function animateHeight( list, from, to ) {
	if ( reducedMotion() ) {
		return;
	}

	list.style.overflow = 'hidden';
	list.style.height = `${ from }px`;

	// Force the start height to be applied before the target is set, or the
	// browser coalesces the two and there is nothing to transition between.
	void list.offsetHeight;

	list.style.transition = 'height 320ms cubic-bezier(0.4, 0, 0.2, 1)';
	list.style.height = `${ to }px`;

	const done = () => {
		list.style.removeProperty( 'height' );
		list.style.removeProperty( 'overflow' );
		list.style.removeProperty( 'transition' );
		list.removeEventListener( 'transitionend', done );
	};

	list.addEventListener( 'transitionend', done );
}

/**
 * The Books & Media "View All" reveal.
 */
function initBooks() {
	const toggle = document.querySelector( '.dn-speaker__books-toggle' );
	const list = document.getElementById( 'dn-speaker-books' );

	if ( ! toggle || ! list ) {
		return;
	}

	const overflow = Array.from( list.querySelectorAll( '.is-overflow' ) );

	if ( ! overflow.length ) {
		return;
	}

	// It ships hidden so a browser without JS never sees a control that would
	// hide covers it cannot bring back.
	toggle.hidden = false;

	toggle.addEventListener( 'click', () => {
		const expanded = 'true' === toggle.getAttribute( 'aria-expanded' );
		const from = list.getBoundingClientRect().height;

		overflow.forEach( ( item ) => {
			item.hidden = expanded;
		} );

		toggle.setAttribute( 'aria-expanded', expanded ? 'false' : 'true' );

		animateHeight( list, from, list.scrollHeight );
	} );
}

function init() {
	const dialog = document.getElementById( 'dn-speaker-lightbox' );

	if ( dialog ) {
		initLightbox( dialog );
	}

	initBooks();
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
